/**
 * Instructor Submission View Component
 * Displays detailed quiz submission results for instructors
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiCheckCircle, FiXCircle, FiArrowLeft, FiClock, FiAward, FiEdit3, FiSave, FiX, FiUnlock } from 'react-icons/fi';
import quizApiService from '../../services/quizApi';
import { sanitizeHtmlForViewer } from '../../lib/richtext/htmlSanitizer';
import { useAuth } from '../../context/AuthContext';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 2px solid var(--primary-color);
  background: var(--background-primary);
  color: var(--primary-color);
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 2rem;

  &:hover {
    background: var(--primary-color);
    color: var(--text-inverse);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(216, 184, 137, 0.3);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    margin-bottom: 1.5rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    
    ${BackButton} {
      width: 100%;
      margin-bottom: 0.75rem;
    }
  }
`;

const Header = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: var(--background-primary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  padding: 1.5rem 2rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 1024px) {
    padding: 1.5rem 1.5rem;
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
    margin-bottom: 1.25rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.25rem 0.75rem;
  }
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  font-family: 'Inter', sans-serif;
  letter-spacing: -0.025em;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.125rem;
  }
`;

const IntroMessage = styled.p`
  font-size: 0.875rem;
  color: var(--text-primary);
  line-height: 1.6;
  margin: 0;
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const QuestionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--background-primary);
  border-radius: 0;
  border: 1px solid var(--border-color);
`;

const GroupHeader = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
`;

const CorrectIncorrectRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const CorrectIncorrectItem = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const QuizDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.75rem 1rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  line-height: 1.5;
  margin-top: 1rem;
  padding: 1.25rem;
  background: var(--background-secondary);
  border-radius: 0;
  border: 1px solid var(--border-color);

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 1rem;
    
    ${QuestionGroup} {
      grid-column: 1 / -1 !important;
    }
  }

  @media (max-width: 480px) {
    padding: 0.875rem;
    gap: 0.875rem;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
  
  @media (max-width: 768px) {
    flex-direction: row;
    align-items: baseline;
    gap: 1rem;
    padding-bottom: 0.875rem;
    border-bottom: 1px solid var(--border-color);
    
    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
  }
`;

const DetailLabel = styled.strong`
  font-weight: 600;
  color: var(--text-muted);
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 0.8125rem;
    text-transform: none;
    letter-spacing: normal;
    color: var(--text-secondary);
    font-weight: 500;
    flex-shrink: 0;
    min-width: 90px;
    white-space: normal;
  }
`;

const DetailValue = styled.span`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.8125rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 768px) {
    text-align: left;
    flex: 1;
    font-weight: 600;
    color: var(--text-primary);
    word-break: break-word;
    white-space: normal;
  }
`;

const QuestionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const QuestionCard = styled(motion.div)`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  transition: var(--transition);

  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-1px);
  }
`;

const QuestionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const QuestionNumber = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
`;

const QuestionPoints = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    white-space: nowrap;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const ExtraCreditBadge = styled.span`
  display: inline-block;
  margin-left: 0.75rem;
  padding: 0.25rem 0.5rem;
  background: linear-gradient(135deg, var(--warning-color), var(--warning-dark));
  color: var(--text-inverse);
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 0;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  box-shadow: 0 2px 4px rgba(245, 158, 11, 0.3);
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    margin-left: 0.5rem;
    padding: 0.2rem 0.4rem;
    font-size: 0.7rem;
  }
  
  @media (max-width: 480px) {
    margin-left: 0.25rem;
    padding: 0.15rem 0.3rem;
    font-size: 0.65rem;
  }
`;

const QuestionPrompt = styled.div`
  font-size: 1rem;
  line-height: 1.6;
  color: var(--text-primary);
  margin-bottom: 1rem;
  font-family: 'Inter', sans-serif;
`;

const AnswerSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const AnswerLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
`;

const AnswerText = styled.div`
  padding: 0.75rem;
  background: var(--background-secondary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  font-size: 0.875rem;
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
`;

const CorrectAnswer = styled.div`
  padding: 0.75rem;
  background: rgba(34, 197, 94, 0.1);
  border-radius: 0;
  border: 1px solid rgba(34, 197, 94, 0.3);
  font-size: 0.875rem;
  color: var(--success-color);
  font-weight: 500;
  font-family: 'Inter', sans-serif;
`;

const IncorrectAnswer = styled.div`
  padding: 0.75rem;
  background: rgba(252, 129, 129, 0.1);
  border-radius: 0;
  border: 1px solid rgba(252, 129, 129, 0.3);
  font-size: 0.875rem;
  color: var(--error-color);
  font-weight: 500;
  font-family: 'Inter', sans-serif;
`;

const StatusIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.correct ? 'var(--success-color)' : 'var(--error-color)'};
  font-family: 'Inter', sans-serif;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--danger-color);
`;

const EditButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid var(--primary-color);
  background: var(--primary-color);
  color: var(--text-inverse);
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
  font-family: 'Inter', sans-serif;

  &:hover {
    background: var(--primary-hover);
    border-color: var(--primary-hover);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
    min-width: auto;
    white-space: nowrap;
  }
  
  @media (max-width: 480px) {
    padding: 0.4rem 0.6rem;
    font-size: 0.75rem;
  }
`;

const EditInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  background: var(--background-primary);
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const EditTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  background: var(--background-primary);
  color: var(--text-primary);
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }

  &.error {
    border-color: var(--danger-color);
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;

  &:hover {
    background: var(--background-secondary);
    border-color: var(--primary-color);
  }

  &.selected {
    background: rgba(216, 184, 137, 0.1);
    border-color: var(--primary-color);
  }
`;

const RadioInput = styled.input`
  margin: 0;
  cursor: pointer;
`;

const RadioText = styled.span`
  font-size: 0.875rem;
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
`;

const ErrorMessage = styled.div`
  color: var(--danger-color);
  font-size: 0.875rem;
  margin-top: 0.5rem;
  font-family: 'Inter', sans-serif;
`;

const RequiredField = styled.span`
  color: var(--danger-color);
  margin-left: 0.25rem;
`;

const FillBlankEditContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const FillBlankEditField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FillBlankLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
`;

const MatchingEditContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const MatchingEditRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  align-items: center;
  padding: 0.75rem;
  background: var(--background-secondary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const MatchingTerm = styled.div`
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const MatchingSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  background: var(--background-primary);
  color: var(--text-primary);
  cursor: pointer;
  width: 100%;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  &.error {
    border-color: var(--error-color);
  }
`;

const EditActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const OverrideIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: rgba(216, 184, 137, 0.1);
  border: 1px solid rgba(216, 184, 137, 0.3);
  border-radius: 0;
  font-size: 0.875rem;
  color: var(--primary-color);
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  margin-bottom: 1rem;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid ${props => props.active ? 'var(--primary-color)' : 'var(--border-color)'};
  background: ${props => props.active 
    ? 'var(--primary-color)' 
    : 'var(--background-secondary)'};
  color: ${props => props.active ? 'white' : 'var(--text-primary)'};
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: ${props => props.active ? '600' : '500'};
  cursor: pointer;
  transition: var(--transition);
  font-family: 'Inter', sans-serif;
  box-shadow: ${props => props.active 
    ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
    : 'none'};

  &:hover {
    background: ${props => props.active 
      ? 'var(--primary-hover)' 
      : 'var(--background-tertiary)'};
    border-color: ${props => props.active 
      ? 'var(--primary-hover)' 
      : 'var(--border-dark)'};
    color: ${props => props.active ? 'white' : 'var(--text-primary)'};
    transform: ${props => props.active ? 'none' : 'translateY(-1px)'};
    box-shadow: ${props => props.active 
      ? '0 2px 6px rgba(0, 0, 0, 0.3)' 
      : '0 2px 4px rgba(0, 0, 0, 0.15)'};
  }

  &:active {
    transform: translateY(0);
  }
`;

const PermissionsModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0;
  
  &:hover {
    background: var(--background-tertiary);
  }
`;

const PermissionSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: var(--text-primary);
`;

const QuestionCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 0;
  transition: background 0.2s;
  
  &:hover {
    background: var(--background-tertiary);
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);
    
    &:hover {
      background: var(--primary-hover);
    }
  }
  
  &.secondary {
    background: var(--background-secondary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    
    &:hover {
      background: var(--background-tertiary);
    }
  }
`;

const PermissionsButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1rem;
  
  &:hover {
    background: var(--primary-hover);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(216, 184, 137, 0.3);
  }
`;

function InstructorSubmissionView() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const fromPendingGrading = searchParams.get('from') === 'pending-grading';
  const isAdmin = user?.role === 'admin';
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [overrideReason, setOverrideReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [answerFilter, setAnswerFilter] = useState('all'); // 'all', 'correct', 'incorrect'
  const [validationErrors, setValidationErrors] = useState({});
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [permissions, setPermissions] = useState({
    allowRetakeAll: false,
    retakeQuestionIds: [],
    allowEditAll: false,
    editQuestionIds: []
  });
  const [savingPermissions, setSavingPermissions] = useState(false);

  const fetchSubmissionDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get attempt details
      const attemptResponse = await quizApiService.getAttemptDetails(attemptId);
      if (attemptResponse.success) {
        setSubmission(attemptResponse.data);
      } else {
        setError(attemptResponse.message || 'Failed to load submission details');
      }
    } catch (err) {
      console.error('Error fetching submission details:', err);
      setError('Failed to load submission details');
    } finally {
      setLoading(false);
    }
  }, [attemptId]);

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await quizApiService.getAttemptPermissions(attemptId);
      if (response.success) {
        setPermissions({
          allowRetakeAll: response.data.allowRetakeAll || false,
          retakeQuestionIds: response.data.retakeQuestionIds || [],
          allowEditAll: response.data.allowEditAll || false,
          editQuestionIds: response.data.editQuestionIds || []
        });
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
    }
  }, [attemptId]);

  useEffect(() => {
    if (attemptId) {
      fetchSubmissionDetails();
      if (isAdmin) {
        fetchPermissions();
      }
    }
  }, [attemptId, fetchSubmissionDetails, fetchPermissions, isAdmin]);

  const handleSavePermissions = async () => {
    try {
      setSavingPermissions(true);
      const response = await quizApiService.updateAttemptPermissions(attemptId, permissions);
      if (response.success) {
        setShowPermissionsModal(false);
        setPermissions(response.data);
        // Show success message or notification
      } else {
        alert('Failed to update permissions');
      }
    } catch (err) {
      console.error('Error saving permissions:', err);
      alert('Failed to update permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  const toggleRetakeQuestion = (questionId) => {
    setPermissions(prev => ({
      ...prev,
      retakeQuestionIds: prev.retakeQuestionIds.includes(questionId)
        ? prev.retakeQuestionIds.filter(id => id !== questionId)
        : [...prev.retakeQuestionIds, questionId]
    }));
  };

  const toggleEditQuestion = (questionId) => {
    setPermissions(prev => ({
      ...prev,
      editQuestionIds: prev.editQuestionIds.includes(questionId)
        ? prev.editQuestionIds.filter(id => id !== questionId)
        : [...prev.editQuestionIds, questionId]
    }));
  };


  // const getQuestionType = (type) => {
  //   switch (type) {
  //     case 'multiple_choice': return 'Multiple Choice';
  //     case 'true_false': return 'True/False';
  //     case 'fill_blank': return 'Fill in the Blank';
  //     default: return type;
  //   }
  // };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question.question_id);
    
    let editValues = {
      answer_text: question.response_data?.answer_text || '',
      selected_option_id: question.response_data?.selected_option_id || null
    };
    
    // For fill-in-the-blank questions, parse the answer into separate blanks
    if (question.question_type === 'fill_blank') {
      const blankCount = getBlankCount(question.question_prompt);
      const parsedAnswers = parseFillBlankAnswer(question.response_data?.answer_text || '');
      
      // Initialize answer_texts array with the correct number of blanks
      editValues.answer_texts = Array(blankCount).fill('').map((_, index) => 
        parsedAnswers[index] || ''
      );
    }
    
    // For matching questions, parse the JSON pairs into matchingPairs object
    if (question.question_type === 'matching') {
      try {
        let pairsToEdit = [];
        let foundAnswer = false;
        
        // First, try to get the original answer from response_data (this is the raw format)
        if (question.response_data?.answer_text) {
          try {
            const answerText = question.response_data.answer_text.trim();
            if (answerText) {
              const parsed = JSON.parse(answerText);
              if (Array.isArray(parsed) && parsed.length > 0) {
                // Validate that it has the correct structure
                if (parsed[0].leftIndex !== undefined && parsed[0].rightIndex !== undefined) {
                  pairsToEdit = parsed;
                  foundAnswer = true;
                }
              }
            }
          } catch (e) {
            console.warn('Failed to parse response_data.answer_text for matching question:', e);
          }
        }
        
        // If we don't have response_data, try your_answer
        if (!foundAnswer) {
          if (Array.isArray(question.your_answer) && question.your_answer.length > 0) {
            // Check if it's formatted with left/right or raw with leftIndex/rightIndex
            if (question.your_answer[0].left && question.your_answer[0].right) {
              // It's formatted - we can't convert back without settings, so use empty array
              // The user will need to re-enter the matches
              console.warn('Matching answer is in formatted format, cannot convert back to indices without question settings');
              pairsToEdit = [];
            } else if (question.your_answer[0].leftIndex !== undefined || question.your_answer[0].rightIndex !== undefined) {
              // It's already in index format
              pairsToEdit = question.your_answer;
              foundAnswer = true;
            }
          } else if (typeof question.your_answer === 'string' && question.your_answer.trim()) {
            // If it's a string, try to parse it
            try {
              const parsed = JSON.parse(question.your_answer);
              if (Array.isArray(parsed) && parsed.length > 0) {
                if (parsed[0].leftIndex !== undefined && parsed[0].rightIndex !== undefined) {
                  pairsToEdit = parsed;
                  foundAnswer = true;
                }
              }
            } catch (e) {
              console.warn('Failed to parse your_answer string for matching question:', e);
            }
          }
        }
        
        // Store pairs as an object map for easier editing: { leftIndex: rightIndex }
        const matchingPairs = {};
        pairsToEdit.forEach(pair => {
          if (pair.leftIndex !== undefined && pair.rightIndex !== undefined) {
            matchingPairs[pair.leftIndex] = pair.rightIndex;
          }
        });
        
        editValues.matchingPairs = matchingPairs;
      } catch (e) {
        console.error('Error processing matching question answer:', e);
        editValues.matchingPairs = {};
      }
    }
    
    setEditValues(editValues);
    setOverrideReason(question.response_data?.instructor_override_reason || '');
    setValidationErrors({});
  };

  const handleCancelEdit = () => {
    setEditingQuestion(null);
    setEditValues({});
    setOverrideReason('');
    setValidationErrors({});
  };

  const handleSaveOverride = async (questionId) => {
    console.log('🚀 Starting override process...');
    console.log('📋 Question ID:', questionId);
    console.log('📋 Attempt ID:', attemptId);
    console.log('📋 Override reason:', overrideReason);
    console.log('📋 Edit values:', editValues);
    console.log('📋 Submission data:', submission);
    
    // Validate inputs
    const errors = {};
    
    if (!overrideReason.trim()) {
      errors.overrideReason = 'Override reason is required';
    }
    
    const question = submission.feedback.find(f => f.question_id === questionId);
    console.log('📋 Found question:', question);
    
    if (question) {
      if (question.question_type === 'multiple_choice' || question.question_type === 'true_false') {
        if (!editValues.selected_option_id) {
          errors.answer = 'Please select an option';
        }
      } else if (question.question_type === 'fill_blank') {
        // For fill-in-the-blank, check if we have answer_texts array
        if (editValues.answer_texts) {
          // Check if at least one blank is filled
          const hasAnswer = editValues.answer_texts.some(answer => answer && answer.trim());
          if (!hasAnswer) {
            errors.answer = 'At least one blank must be filled';
          }
        } else if (!editValues.answer_text || !editValues.answer_text.trim()) {
          errors.answer = 'Answer text is required';
        }
      } else if (question.question_type === 'matching') {
        // For matching, check if matchingPairs object has valid pairs
        if (!editValues.matchingPairs || Object.keys(editValues.matchingPairs).length === 0) {
          errors.answer = 'Please match all items';
        } else {
          // Check that all pairs have valid rightIndex values
          const hasInvalidPairs = Object.values(editValues.matchingPairs).some(
            rightIndex => rightIndex === undefined || rightIndex === null
          );
          if (hasInvalidPairs) {
            errors.answer = 'Please match all items';
          }
        }
      }
    }
    
    console.log('📋 Validation errors:', errors);
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setSaving(false);
      console.log('❌ Validation failed, stopping override');
      return;
    }
    
    try {
      setSaving(true);
      setValidationErrors({});
      
    // Format the answer properly for the API
    let answerText = editValues.answer_text;
    if (editValues.answer_texts) {
      answerText = formatFillBlankAnswer(editValues.answer_texts);
    } else if (question.question_type === 'matching') {
      // For matching questions, convert matchingPairs object to JSON array
      if (editValues.matchingPairs) {
        const pairs = Object.entries(editValues.matchingPairs)
          .filter(([leftIndex, rightIndex]) => rightIndex !== undefined && rightIndex !== null)
          .map(([leftIndex, rightIndex]) => ({
            leftIndex: parseInt(leftIndex),
            rightIndex: parseInt(rightIndex)
          }));
        answerText = JSON.stringify(pairs);
      } else {
        answerText = '[]';
      }
    }
      
      const requestData = {
        answer_text: answerText,
        selected_option_id: editValues.selected_option_id,
        reason: overrideReason
      };
      
      console.log('🔍 Override request data:', requestData);
      console.log('🔍 Question type:', question?.question_type);
      console.log('🔍 Edit values:', editValues);
      console.log('🔍 API endpoint:', `/quizzes/attempts/${attemptId}/responses/${questionId}/override`);
      
      const response = await quizApiService.overrideStudentAnswer(attemptId, questionId, requestData);
      console.log('📡 API response:', response);

      if (response.success) {
        console.log('✅ Override successful, refreshing data...');
        // Refresh submission data
        await fetchSubmissionDetails();
        setEditingQuestion(null);
        setEditValues({});
        setOverrideReason('');
        console.log('✅ Override process completed successfully');
      } else {
        console.error('❌ Override failed:', response);
        setError(response.message || 'Failed to override answer');
      }
    } catch (err) {
      console.error('❌ Error overriding answer:', err);
      console.error('❌ Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name,
        response: err.response
      });
      setError('Failed to override answer');
    } finally {
      setSaving(false);
      console.log('🏁 Override process finished');
    }
  };

  const getFilteredQuestions = () => {
    if (!submission?.feedback) return [];
    
    switch (answerFilter) {
      case 'correct':
        return submission.feedback.filter(q => q.is_correct === true);
      case 'incorrect':
        return submission.feedback.filter(q => q.is_correct === false || q.is_correct === null);
      default:
        return submission.feedback;
    }
  };

  const getBlankCount = (prompt) => {
    if (!prompt) return 0;
    return (prompt.match(/_/g) || []).length;
  };

  const parseFillBlankAnswer = (answerText) => {
    if (!answerText) return [];
    return answerText.split('|');
  };

  const formatFillBlankAnswer = (answers) => {
    if (!answers || answers.length === 0) return '';
    return answers.join('|');
  };

  const formatFillBlankPrompt = (prompt, studentAnswers) => {
    let formattedPrompt = prompt;
    let answerIndex = 0;
    
    // Handle both string and array answers
    let answers = [];
    if (Array.isArray(studentAnswers)) {
      answers = studentAnswers;
    } else if (typeof studentAnswers === 'string' && studentAnswers !== '') {
      // For single string answers, split by pipe if it contains multiple answers
      answers = studentAnswers.includes('|') ? studentAnswers.split('|') : [studentAnswers];
    }
    
    // Replace each underscore with the corresponding student answer
    formattedPrompt = formattedPrompt.replace(/_/g, (match) => {
      if (answerIndex < answers.length) {
        const answer = answers[answerIndex];
        answerIndex++;
        
        // If student provided an answer, show it
        if (answer && answer !== '') {
          return `<span style="color: var(--text-primary); font-weight: bold; background: rgba(148, 163, 184, 0.1); padding: 0.25rem 0.5rem; border-radius: 0; border: 1px solid rgba(148, 163, 184, 0.3);">${answer}</span>`;
        }
      }
      
      // If no answer provided, show underscores
      return '________';
    });
    
    return formattedPrompt;
  };

  if (loading) {
    return (
      <Container>
        <LoadingState>
          <FiClock size={48} />
          <h3>Loading submission details...</h3>
          <p>Please wait while we fetch the submission information.</p>
        </LoadingState>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <BackButton onClick={() => navigate(-1)}>
          <FiArrowLeft size={16} />
          Back
        </BackButton>
        <ErrorState>
          <FiXCircle size={48} />
          <h3>Error loading submission</h3>
          <p>{error}</p>
        </ErrorState>
      </Container>
    );
  }

  if (!submission) {
    return (
      <Container>
        <BackButton onClick={() => navigate(-1)}>
          <FiArrowLeft size={16} />
          Back
        </BackButton>
        <ErrorState>
          <FiXCircle size={48} />
          <h3>Submission not found</h3>
          <p>The requested submission could not be found.</p>
        </ErrorState>
      </Container>
    );
  }

  const percentage = submission.max_score > 0 
    ? (submission.score / submission.max_score) * 100 
    : 0;
  
  // Ensure percentage is a valid number and format it properly (ensure it's an integer with no decimals)
  const displayPercentage = isNaN(percentage) ? 0 : parseInt(Math.round(percentage).toString(), 10);

  // Calculate correct/incorrect for regular questions (non-extra-credit)
  const regularCorrect = submission.feedback && Array.isArray(submission.feedback)
    ? submission.feedback.filter(q => {
        if (q.extra_credit) return false;
        if (q.points_awarded !== undefined) {
          return Number(q.points_awarded) > 0;
        }
        return q.is_correct === true || q.is_correct === 1;
      }).length
    : 0;
  
  const regularIncorrect = submission.feedback && Array.isArray(submission.feedback)
    ? submission.feedback.filter(q => {
        if (q.extra_credit) return false;
        if (q.points_awarded !== undefined) {
          return Number(q.points_awarded) === 0;
        }
        return q.is_correct === false || q.is_correct === 0;
      }).length
    : 0;

  // Calculate correct/incorrect for extra credit questions
  const extraCreditCorrect = submission.feedback && Array.isArray(submission.feedback)
    ? submission.feedback.filter(q => {
        if (!q.extra_credit) return false;
        if (q.points_awarded !== undefined) {
          return Number(q.points_awarded) > 0;
        }
        return q.is_correct === true || q.is_correct === 1;
      }).length
    : 0;
  
  const extraCreditIncorrect = submission.feedback && Array.isArray(submission.feedback)
    ? submission.feedback.filter(q => {
        if (!q.extra_credit) return false;
        if (q.points_awarded !== undefined) {
          return Number(q.points_awarded) === 0;
        }
        return q.is_correct === false || q.is_correct === 0;
      }).length
    : 0;

  // Calculate total regular points (sum of points_awarded for non-extra-credit questions)
  const totalRegularPoints = submission.feedback && Array.isArray(submission.feedback)
    ? submission.feedback
        .filter(q => !q.extra_credit)
        .reduce((sum, q) => sum + (Number(q.points_awarded) || 0), 0)
    : 0;

  // Calculate total extra credit points (sum of points_awarded for extra-credit questions)
  const totalExtraCreditPoints = submission.feedback && Array.isArray(submission.feedback)
    ? submission.feedback
        .filter(q => q.extra_credit)
        .reduce((sum, q) => sum + (Number(q.points_awarded) || 0), 0)
    : 0;

  // Format date helper
  const formatDateShort = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container>
      <ButtonContainer>
        {fromPendingGrading && (
          <BackButton onClick={() => navigate('/instructor/gradebook?tab=pending-grading')}>
            <FiArrowLeft size={16} />
            Back to Pending Grading
          </BackButton>
        )}
        <BackButton onClick={() => navigate(-1)}>
          <FiArrowLeft size={16} />
          Back to Submissions
        </BackButton>
      </ButtonContainer>

      <Header
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Title>{submission.quiz_title || 'Quiz Submission'}</Title>
        
        <IntroMessage>
          Say Bismillah and do your best. Alhamdulilah, you have learned a lot during this semester. Do your best to put what you learned into practice. May Allah grant us all success in this dunya and the aakhirah.
        </IntroMessage>

        <QuizDetails>
          {/* Row 1: Due, Time limit, Started, Completed, Score */}
          <DetailItem>
            <DetailLabel>Due</DetailLabel>
            <DetailValue>{submission.due_at ? formatDateShort(submission.due_at) : 'N/A'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Time limit</DetailLabel>
            <DetailValue>{submission.time_limit_minutes ? `${submission.time_limit_minutes} min` : 'No limit'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>STARTED</DetailLabel>
            <DetailValue>{formatDateShort(submission.started_at)}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>COMPLETED</DetailLabel>
            <DetailValue>{formatDateShort(submission.submitted_at)}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>SCORE</DetailLabel>
            <DetailValue>
              {`${Math.round(submission.score || 0)}/${Math.round(submission.max_score || 0)} (${displayPercentage}%)`}
              {submission.is_late && (
                <span style={{ 
                  color: 'var(--warning-color)', 
                  marginLeft: '0.5rem', 
                  fontSize: '0.875rem'
                }}>
                  (Late Submission)
                </span>
              )}
            </DetailValue>
          </DetailItem>

          {/* Regular Questions Group */}
          <QuestionGroup style={{ gridColumn: '1 / 3' }}>
            <GroupHeader>Regular Questions</GroupHeader>
            <CorrectIncorrectRow>
              <CorrectIncorrectItem>
                <DetailLabel>Correct</DetailLabel>
                <DetailValue>{regularCorrect}</DetailValue>
              </CorrectIncorrectItem>
              <CorrectIncorrectItem>
                <DetailLabel>Incorrect</DetailLabel>
                <DetailValue>{regularIncorrect}</DetailValue>
              </CorrectIncorrectItem>
            </CorrectIncorrectRow>
            <CorrectIncorrectRow style={{ marginTop: '0.5rem' }}>
              <CorrectIncorrectItem>
                <DetailLabel>Total Regular Points</DetailLabel>
                <DetailValue>{Math.round(totalRegularPoints)}</DetailValue>
              </CorrectIncorrectItem>
            </CorrectIncorrectRow>
          </QuestionGroup>

          {/* Extra Credit Questions Group */}
          <QuestionGroup style={{ gridColumn: '3 / 5' }}>
            <GroupHeader>Extra Credit Questions</GroupHeader>
            <CorrectIncorrectRow>
              <CorrectIncorrectItem>
                <DetailLabel>Correct</DetailLabel>
                <DetailValue>{extraCreditCorrect}</DetailValue>
              </CorrectIncorrectItem>
              <CorrectIncorrectItem>
                <DetailLabel>Incorrect</DetailLabel>
                <DetailValue>{extraCreditIncorrect}</DetailValue>
              </CorrectIncorrectItem>
            </CorrectIncorrectRow>
            <CorrectIncorrectRow style={{ marginTop: '0.5rem' }}>
              <CorrectIncorrectItem>
                <DetailLabel>Total Extra Credit Points</DetailLabel>
                <DetailValue>{Math.round(totalExtraCreditPoints)}</DetailValue>
              </CorrectIncorrectItem>
            </CorrectIncorrectRow>
          </QuestionGroup>
        </QuizDetails>
      </Header>

      {isAdmin && (
        <PermissionsButton onClick={() => setShowPermissionsModal(true)}>
          <FiUnlock size={16} />
          Manage Retake/Edit Permissions
        </PermissionsButton>
      )}

      {submission.feedback && submission.feedback.length > 0 && (
        <QuestionsList>
          <FilterContainer>
            <FilterButton 
              active={answerFilter === 'all'} 
              onClick={() => setAnswerFilter('all')}
            >
              All Questions ({submission.feedback.length})
            </FilterButton>
            <FilterButton 
              active={answerFilter === 'correct'} 
              onClick={() => setAnswerFilter('correct')}
            >
              Correct ({submission.feedback.filter(q => q.is_correct === true).length})
            </FilterButton>
            <FilterButton 
              active={answerFilter === 'incorrect'} 
              onClick={() => setAnswerFilter('incorrect')}
            >
              Incorrect ({submission.feedback.filter(q => q.is_correct === false || q.is_correct === null).length})
            </FilterButton>
          </FilterContainer>
          {getFilteredQuestions().map((question, index) => (
            <QuestionCard
              key={question.question_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <QuestionHeader>
                <QuestionNumber>
                  Question {index + 1}
                  {question.extra_credit && (
                    <ExtraCreditBadge>Extra Credit</ExtraCreditBadge>
                  )}
                </QuestionNumber>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '1rem',
                  flexWrap: 'wrap'
                }}>
                  <QuestionPoints>
                    <FiAward size={14} />
                    {question.points_awarded} / {question.max_points} points
                  </QuestionPoints>
                  {editingQuestion !== question.question_id && (
                    <EditButton onClick={() => handleEditQuestion(question)}>
                      <FiEdit3 size={14} />
                      Edit Answer
                    </EditButton>
                  )}
                </div>
              </QuestionHeader>

              <QuestionPrompt>
                {question.question_type === 'fill_blank' 
                  ? <span dangerouslySetInnerHTML={{ 
                      __html: sanitizeHtmlForViewer(formatFillBlankPrompt(question.question_prompt, question.your_answer)) 
                    }} />
                  : question.question_prompt}
              </QuestionPrompt>

              <AnswerSection>
                {question.response_data?.instructor_override === true && (
                  <OverrideIndicator>
                    <FiEdit3 size={14} />
                    Answer overridden by {question.response_data.override_by_name || 'instructor'}
                    {question.response_data.instructor_override_reason && (
                      <span>: {question.response_data.instructor_override_reason}</span>
                    )}
                  </OverrideIndicator>
                )}

                {editingQuestion === question.question_id ? (
                  <>
                    <AnswerLabel>Edit Answer:<RequiredField>*</RequiredField></AnswerLabel>
                    
                    {question.question_type === 'multiple_choice' || question.question_type === 'true_false' ? (
                      <RadioGroup>
                        {question.options?.map((option, index) => (
                          <RadioOption 
                            key={option.id || index}
                            className={editValues.selected_option_id === option.id ? 'selected' : ''}
                          >
                            <RadioInput
                              type="radio"
                              name={`question_${question.question_id}`}
                              value={option.id}
                              checked={editValues.selected_option_id === option.id}
                              onChange={(e) => setEditValues({...editValues, selected_option_id: parseInt(e.target.value)})}
                            />
                            <RadioText>{option.text}</RadioText>
                          </RadioOption>
                        ))}
                      </RadioGroup>
                    ) : question.question_type === 'fill_blank' ? (
                      <FillBlankEditContainer>
                        {editValues.answer_texts ? (
                          editValues.answer_texts.map((answer, blankIndex) => (
                            <FillBlankEditField key={blankIndex}>
                              <FillBlankLabel>Blank Answer {blankIndex + 1}:</FillBlankLabel>
                              <EditInput
                                value={answer}
                                onChange={(e) => {
                                  const newAnswers = [...editValues.answer_texts];
                                  newAnswers[blankIndex] = e.target.value;
                                  setEditValues({...editValues, answer_texts: newAnswers});
                                }}
                                placeholder={`Enter answer for blank ${blankIndex + 1}...`}
                                className={validationErrors.answer ? 'error' : ''}
                              />
                            </FillBlankEditField>
                          ))
                        ) : (
                          <EditTextarea
                            value={editValues.answer_text || ''}
                            onChange={(e) => setEditValues({...editValues, answer_text: e.target.value})}
                            placeholder="Enter the answer..."
                            className={validationErrors.answer ? 'error' : ''}
                          />
                        )}
                      </FillBlankEditContainer>
                    ) : question.question_type === 'matching' ? (
                      (() => {
                        const settings = question.settings || {};
                        const leftItems = settings.leftItems || [];
                        const rightItems = settings.rightItems || [];
                        
                        if (leftItems.length === 0 || rightItems.length === 0) {
                          return (
                            <div style={{ 
                              padding: '1rem', 
                              background: 'rgba(246, 224, 94, 0.1)', 
                              borderRadius: 0,
                              color: 'var(--warning-dark)',
                              fontSize: '0.875rem'
                            }}>
                              Question settings not available. Please use JSON format to edit.
                              <EditTextarea
                                value={editValues.answer_text || '[]'}
                                onChange={(e) => setEditValues({...editValues, answer_text: e.target.value})}
                                placeholder='Enter JSON array of pairs, e.g., [{"leftIndex": 0, "rightIndex": 1}]'
                                className={validationErrors.answer ? 'error' : ''}
                                style={{ minHeight: '100px', fontFamily: 'monospace', marginTop: '0.5rem' }}
                              />
                            </div>
                          );
                        }
                        
                        // Filter out empty items
                        const validLeftItems = leftItems.map((item, index) => ({ item, index })).filter(({ item }) => item && item.trim());
                        const validRightItems = rightItems.map((item, index) => ({ item, index })).filter(({ item }) => item && item.trim());
                        
                        return (
                          <MatchingEditContainer>
                            {validLeftItems.map(({ item: leftItem, index: leftIndex }) => {
                              const currentRightIndex = editValues.matchingPairs?.[leftIndex] !== undefined 
                                ? editValues.matchingPairs[leftIndex] 
                                : '';
                              
                              return (
                                <MatchingEditRow key={leftIndex}>
                                  <MatchingTerm>{leftItem}</MatchingTerm>
                                  <MatchingSelect
                                    value={currentRightIndex}
                                    onChange={(e) => {
                                      const newRightIndex = e.target.value === '' ? undefined : parseInt(e.target.value);
                                      const newPairs = { ...editValues.matchingPairs };
                                      
                                      if (newRightIndex === undefined) {
                                        delete newPairs[leftIndex];
                                      } else {
                                        newPairs[leftIndex] = newRightIndex;
                                      }
                                      
                                      setEditValues({...editValues, matchingPairs: newPairs});
                                      
                                      // Clear validation error when user makes a selection
                                      if (validationErrors.answer) {
                                        setValidationErrors({...validationErrors, answer: undefined});
                                      }
                                    }}
                                    className={validationErrors.answer ? 'error' : ''}
                                  >
                                    <option value="">-- Select Definition --</option>
                                    {validRightItems.map(({ item: rightItem, index: rightIndex }) => (
                                      <option key={rightIndex} value={rightIndex}>
                                        {rightItem}
                                      </option>
                                    ))}
                                  </MatchingSelect>
                                </MatchingEditRow>
                              );
                            })}
                          </MatchingEditContainer>
                        );
                      })()
                    ) : (
                      <EditInput
                        value={editValues.answer_text || ''}
                        onChange={(e) => setEditValues({...editValues, answer_text: e.target.value})}
                        placeholder="Enter the answer..."
                        className={validationErrors.answer ? 'error' : ''}
                      />
                    )}
                    
                    {validationErrors.answer && (
                      <ErrorMessage>{validationErrors.answer}</ErrorMessage>
                    )}
                    
                    <AnswerLabel>Override Reason:<RequiredField>*</RequiredField></AnswerLabel>
                    <EditTextarea
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      placeholder="Explain why you're overriding this answer..."
                      className={validationErrors.overrideReason ? 'error' : ''}
                    />
                    {validationErrors.overrideReason && (
                      <ErrorMessage>{validationErrors.overrideReason}</ErrorMessage>
                    )}

                    <EditActions>
                      <EditButton 
                        onClick={() => handleSaveOverride(question.question_id)}
                        disabled={saving}
                      >
                        <FiSave size={14} />
                        {saving ? 'Saving...' : 'Save Override'}
                      </EditButton>
                      <EditButton 
                        onClick={handleCancelEdit}
                        style={{ background: 'var(--text-secondary)', borderColor: 'var(--text-secondary)' }}
                      >
                        <FiX size={14} />
                        Cancel
                      </EditButton>
                    </EditActions>
                  </>
                ) : (
                  <>
                    <AnswerLabel>Your Answer:</AnswerLabel>
                    {(() => {
                      // Check if there's a valid answer
                      if (question.your_answer === null || question.your_answer === undefined) return false;
                      
                      // For matching questions, check if array has valid pairs
                      if (question.question_type === 'matching') {
                        if (Array.isArray(question.your_answer)) {
                          // Check if array has at least one valid pair object
                          return question.your_answer.length > 0 && 
                                 question.your_answer.some(pair => pair && (pair.left || pair.right));
                        }
                        return false;
                      }
                      
                      // For other question types
                      // Check if answer exists and is not empty
                      if (Array.isArray(question.your_answer)) {
                        // For arrays, check if it has valid content
                        if (question.your_answer.length === 0) return false;
                        // For matching questions, check if pairs have content
                        if (question.question_type === 'matching') {
                          return question.your_answer.some(pair => pair && (pair.left || pair.right));
                        }
                        // For multiple choice arrays, check if any element is truthy
                        return question.your_answer.some(answer => answer && answer.trim && answer.trim() !== '');
                      }
                      // Check for empty string, null, undefined, or the number 0
                      if (question.your_answer === '' || question.your_answer === null || question.your_answer === undefined || question.your_answer === 0) return false;
                      return true;
                    })() ? (
                      question.is_correct ? (
                        <AnswerText>
                          {question.question_type === 'matching' && Array.isArray(question.your_answer) ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                              {question.your_answer.map((pair, index) => {
                                const isCorrect = pair.isCorrect !== false; // Default to true if not specified
                                
                                return (
                                  <div key={index} style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: '0.25rem',
                                    wordWrap: 'break-word', 
                                    overflowWrap: 'break-word'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                      {isCorrect ? (
                                        <FiCheckCircle size={18} style={{ color: 'var(--success-color)', flexShrink: 0 }} />
                                      ) : (
                                        <FiXCircle size={18} style={{ color: 'var(--error-color)', flexShrink: 0 }} />
                                      )}
                                      <div><strong>Term:</strong> {pair.left}</div>
                                    </div>
                                    <div><strong>Definition:</strong> {pair.right}</div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : Array.isArray(question.your_answer) 
                            ? question.your_answer.join(', ') 
                            : question.your_answer}
                        </AnswerText>
                      ) : (
                        <IncorrectAnswer>
                          {question.question_type === 'matching' && Array.isArray(question.your_answer) ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                              {question.your_answer.map((pair, index) => {
                                const isCorrect = pair.isCorrect !== false; // Default to true if not specified
                                
                                return (
                                  <div key={index} style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: '0.25rem',
                                    wordWrap: 'break-word', 
                                    overflowWrap: 'break-word'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                      {isCorrect ? (
                                        <FiCheckCircle size={18} style={{ color: 'var(--success-color)', flexShrink: 0 }} />
                                      ) : (
                                        <FiXCircle size={18} style={{ color: 'var(--error-color)', flexShrink: 0 }} />
                                      )}
                                      <div><strong>Term:</strong> {pair.left}</div>
                                    </div>
                                    <div><strong>Definition:</strong> {pair.right}</div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : Array.isArray(question.your_answer) 
                            ? question.your_answer.join(', ') 
                            : question.your_answer}
                        </IncorrectAnswer>
                      )
                    ) : (
                      <AnswerText style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>
                        {question.question_type === 'multiple_choice' ? 'No answer selected' : 'No answer provided'}
                      </AnswerText>
                    )}

                    <AnswerLabel>Correct Answer:</AnswerLabel>
                    <CorrectAnswer>
                      {question.question_type === 'fill_blank' && Array.isArray(question.correct_answer) ? (
                        question.correct_answer.map((answer, index) => (
                          <div key={index} style={{ marginBottom: '0.5rem' }}>
                            <strong>Blank Answer {index + 1}:</strong> {answer}
                          </div>
                        ))
                      ) : question.question_type === 'matching' && Array.isArray(question.correct_answer) ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {question.correct_answer.map((pair, index) => (
                            <div key={index} style={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              gap: '0.25rem',
                              wordWrap: 'break-word', 
                              overflowWrap: 'break-word' 
                            }}>
                              <div><strong>Term:</strong> {pair.left}</div>
                              <div><strong>Definition:</strong> {pair.right}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        Array.isArray(question.correct_answer) 
                          ? question.correct_answer.join(', ') 
                          : question.correct_answer
                      )}
                    </CorrectAnswer>

                    <StatusIcon correct={question.is_correct}>
                      {question.is_correct ? (
                        <>
                          <FiCheckCircle size={16} />
                          Correct
                        </>
                      ) : (
                        <>
                          <FiXCircle size={16} />
                          Incorrect
                        </>
                      )}
                    </StatusIcon>
                  </>
                )}
              </AnswerSection>
            </QuestionCard>
          ))}
        </QuestionsList>
      )}

      {showPermissionsModal && (
        <PermissionsModal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowPermissionsModal(false);
            }
          }}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Manage Retake/Edit Permissions</ModalTitle>
              <CloseButton onClick={() => setShowPermissionsModal(false)}>
                <FiX />
              </CloseButton>
            </ModalHeader>

            <PermissionSection>
              <SectionTitle>Retake Permissions</SectionTitle>
              <CheckboxGroup>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={permissions.allowRetakeAll}
                    onChange={(e) => setPermissions(prev => ({
                      ...prev,
                      allowRetakeAll: e.target.checked,
                      retakeQuestionIds: e.target.checked ? [] : prev.retakeQuestionIds
                    }))}
                  />
                  Allow retake for all questions
                </CheckboxLabel>
                {!permissions.allowRetakeAll && submission?.feedback && (
                  <div style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                    {submission.feedback.map((question) => (
                      <QuestionCheckbox key={question.question_id}>
                        <input
                          type="checkbox"
                          checked={permissions.retakeQuestionIds.includes(question.question_id)}
                          onChange={() => toggleRetakeQuestion(question.question_id)}
                        />
                        <span>Question {question.question_order || question.question_id}</span>
                      </QuestionCheckbox>
                    ))}
                  </div>
                )}
              </CheckboxGroup>
            </PermissionSection>

            <PermissionSection>
              <SectionTitle>Edit Permissions</SectionTitle>
              <CheckboxGroup>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={permissions.allowEditAll}
                    onChange={(e) => setPermissions(prev => ({
                      ...prev,
                      allowEditAll: e.target.checked,
                      editQuestionIds: e.target.checked ? [] : prev.editQuestionIds
                    }))}
                  />
                  Allow edit for all questions
                </CheckboxLabel>
                {!permissions.allowEditAll && submission?.feedback && (
                  <div style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                    {submission.feedback.map((question) => (
                      <QuestionCheckbox key={question.question_id}>
                        <input
                          type="checkbox"
                          checked={permissions.editQuestionIds.includes(question.question_id)}
                          onChange={() => toggleEditQuestion(question.question_id)}
                        />
                        <span>Question {question.question_order || question.question_id}</span>
                      </QuestionCheckbox>
                    ))}
                  </div>
                )}
              </CheckboxGroup>
            </PermissionSection>

            <ModalActions>
              <ActionButton
                className="secondary"
                onClick={() => setShowPermissionsModal(false)}
                disabled={savingPermissions}
              >
                Cancel
              </ActionButton>
              <ActionButton
                className="primary"
                onClick={handleSavePermissions}
                disabled={savingPermissions}
              >
                {savingPermissions ? 'Saving...' : 'Save Permissions'}
              </ActionButton>
            </ModalActions>
          </ModalContent>
        </PermissionsModal>
      )}
    </Container>
  );
}

export default InstructorSubmissionView;
